import { useState, useEffect, useRef } from 'react';
import supabase from "./supabase";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { syncHealthWorkouts, syncDailyActivity, syncSleep } from './services/healthSync';
import { syncScreenTime } from './services/screenTime';
import { setSentryUser, reportError, addBreadcrumb } from './sentry';
import resetViewport from './viewportReset';
import { T, themeMode, effectiveTheme } from './theme';
import { configurePurchases, logOutPurchases } from './lib/purchases';
import { routeNotificationClick, writePendingAction, PENDING_BANK_RECONNECT_KEY, BANK_RECONNECT_EVENT } from './lib/pushActions';
import LockGate from './components/AppLock';
import LandingPage from './LandingPage';
import Scorify from './Scorify';
import Auth from './Auth';
import WaitlistPage from './WaitlistPage';
import ResetPassword from './ResetPassword';
import { applyPushIdentity } from './lib/pushIdentity';

const ensurePushPermission = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
    const granted = await OneSignal.Notifications.getPermissionAsync?.();
    if (!granted) {
      await OneSignal.Notifications.requestPermission(true);
    }
    // v5: OS permission and subscription opt-in are separate states, and
    // nothing ever called optIn(). Guarded on permission actually being
    // granted so it can never trigger the native prompt — optIn() only
    // prompts when permission is undetermined, which it cannot be here.
    // Safe no-op when already opted in.
    const nowGranted = await OneSignal.Notifications.getPermissionAsync?.();
    if (nowGranted) { try { OneSignal.User?.pushSubscription?.optIn?.(); } catch {} }
  } catch (e) {
    console.log('push permission request failed', e);
  }
};

// Which external_id this DEVICE last aliased. Deliberately exempt from the
// sign-out localStorage purge (both purge loops), because it must survive
// sign-out to detect the account-switch case below.
const OS_EXT_KEY = 'scorify_onesignal_ext';

const linkOneSignalUser = (userId) => {
  if (!Capacitor.isNativePlatform()) return;
  // ROUTINE SIGN-OUT KEEPS THE ALIAS — this used to call logout(), which
  // detaches the alias from the device's OneSignal user. Every sign-out/
  // sign-in cycle across reinstalls then minted a fresh user record claiming
  // the external_id, and by Sept 2026 five orphaned records were absorbing
  // every cron send (invalid_aliases, delivered-to-zero-devices). The alias
  // now moves only when a DIFFERENT account signs in (below) or on account
  // deletion (Scorify.jsx delete flow, which still logs out).
  if (!userId) return;
  try {
    const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
    const uid = String(userId);

    const doLogin = () => {
      try {
        // Account switch: a different user on the same device — the alias must
        // move. Detected by comparing against the last aliased id this device
        // stored; logout() here is the legitimate use of it. After login() the
        // push subscription is opted back IN (sign-out opts it out, see
        // handleSignOut), but only once OS permission is granted so this can
        // never raise the native prompt (src/lib/pushIdentity.js).
        const prev = (() => { try { return localStorage.getItem(OS_EXT_KEY); } catch { return null; } })();
        try { localStorage.setItem(OS_EXT_KEY, uid); } catch {}
        Promise.resolve(OneSignal.Notifications?.getPermissionAsync?.()).catch(() => false)
          .then((granted) => applyPushIdentity(OneSignal, 'login', { prevAliased: prev, userId: uid, permissionGranted: granted === true }))
          .catch((e) => console.log('OneSignal login error', e));
      } catch (e) { console.log('OneSignal login error', e); }
    };

    // Gate login on the push subscription existing. initialize() only STARTS
    // APNs registration; on a first-ever launch the subscription materializes
    // after an async Apple roundtrip, and login() before that aliases a
    // subscription-less user — the exact orphan-record shape that broke
    // delivery. Mechanism: getIdAsync now, one-shot change observer if absent,
    // and a 10s fallback that logs in ANYWAY — permission-denied or offline
    // users still get aliased (an alias without a subscription attaches the
    // subscription later on the same SDK state; never aliasing is worse).
    const sub = OneSignal.User?.pushSubscription;
    if (!sub?.getIdAsync) { doLogin(); return; } // older SDK surface — old behavior
    sub.getIdAsync().then((id) => {
      if (id) { doLogin(); return; }
      let done = false;
      const finish = () => { if (done) return; done = true; try { sub.removeEventListener('change', onChange); } catch {} doLogin(); };
      const onChange = (ev) => { if (ev?.current?.id) finish(); };
      try { sub.addEventListener('change', onChange); } catch { finish(); return; }
      setTimeout(finish, 10000);
    }).catch(doLogin);
  } catch (e) {
    console.log('OneSignal identity link error', e);
  }
};

// Reads T so the pre-app spinner matches whatever theme the app will render —
// it used to hardcode the dark palette, so every cold start flashed dark→cream.
// On NATIVE it is crimson instead: the cold-launch chain is launch storyboard →
// webview background → this spinner → LockGate's boot cover, all T.accent, so
// the handoff into the lock screen reads as one continuous surface. Web keeps
// the cream version (its neighbor is the cream landing/dashboard, not a
// crimson splash).
const Spinner = ({ offline }) => {
  const native = Capacitor.isNativePlatform();
  const bg = native ? T.accent : T.bg;
  const fg = native ? T.surface : T.accent;
  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex", flexDirection:"column", gap:14, alignItems:"center", justifyContent:"center" }}>
      <span style={{ display:"inline-block", width:36, height:36, border:`3px solid ${fg}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
      {offline && (
        <span style={{ fontSize:13, color:native ? T.surface : T.textMuted, fontFamily:"'DM Sans',sans-serif" }}>
          You're offline. Waiting for a connection…
        </span>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// Whether Supabase still holds a session in localStorage (key shape:
// sb-<project-ref>-auth-token). getSession() with an EXPIRED token offline
// resolves { session: null } even though it deliberately KEEPS the stored
// session (retryable refresh errors never sign the user out) — so this is the
// signal that distinguishes "signed-in user with no signal" from "genuinely
// signed out". A real sign-out removes the key.
const hasStoredSession = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token') && localStorage.getItem(k)) return true;
    }
  } catch { /* storage unavailable — treat as no session */ }
  return false;
};

// Rendered ONLY on /demo — zero auth, zero Supabase, zero async calls
function DemoApp() {
  return (
    <Routes>
      <Route path="/demo" element={<Scorify isDemo={true} />} />
      <Route path="*" element={<Navigate to="/demo" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  const [user, setUser] = useState(undefined);
  // Cold launch, expired token, no signal: getSession() spends its refresh
  // retries (~25s) and then resolves { session: null }, which used to route a
  // signed-in user to the landing page as if they had been logged out. When
  // that exact shape is detected — no session, provably offline, a session
  // still in storage — the spinner is held with an offline line instead, and
  // getSession is re-run when connectivity returns.
  const [authOffline, setAuthOffline] = useState(false);
  const offlineHold = useRef(false);
  const navigate = useNavigate();

  // RevenueCat is bound to the Supabase user id once auth resolves, so the
  // server can map a purchase straight to profiles.id (src/lib/purchases.js;
  // a no-op on web and in demo). Keyed on the id, not the user object, for the
  // same churn reason as setUserStable below.
  useEffect(() => {
    if (!user?.id) return;
    configurePurchases(user.id).catch((e) => reportError(e, { op: 'purchases.configure' }));
  }, [user?.id]);

  // Supabase auto-refreshes the access token (periodically and on foreground),
  // and every refresh fires onAuthStateChange with a NEW user object carrying
  // the SAME id. Passing that new object to <Scorify user={user}> re-ran every
  // effect keyed on the user object — loadData, the Plaid fetch, daily-login
  // XP — on every refresh, and made each cold launch load data twice
  // (resolveSession plus the INITIAL_SESSION event). The token itself is read
  // fresh from supabase.auth.getSession() by apiFetch, never from this object,
  // so keeping the SAME reference when the id is unchanged is safe and stops
  // the churn. Only an actual account change (or sign-out) swaps the object.
  const setUserStable = (nextUser) => {
    setUser((prev) => (prev && nextUser && prev.id === nextUser.id ? prev : (nextUser ?? null)));
  };

  useEffect(() => {
    const resolveSession = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session && navigator.onLine === false && hasStoredSession()) {
          offlineHold.current = true;
          setAuthOffline(true);
          return; // spinner stays up; the listeners below retry
        }
        offlineHold.current = false;
        setAuthOffline(false);
        setUserStable(session?.user ?? null);
        // Supabase user id only — never the email. Lets a crash be traced to an
        // account without putting PII in Sentry.
        setSentryUser(session?.user?.id ?? null);
        linkOneSignalUser(session?.user?.id ?? null);
        if (session?.user?.id) {
          syncHealthWorkouts(session.user.id);
          syncDailyActivity(session.user.id);
          syncSleep(session.user.id);
          // Screen Time: was absent here, so its sync only ran inside
          // Scorify's dataLoaded-gated effect — i.e. cold launch only. A day
          // could close and chain overnight and never reach Supabase until a
          // force-quit. Now in line with the other passive syncs. (Arm/rearm
          // stays in Scorify, which holds the goal + status context; App.js
          // only pushes whatever the extension already recorded.)
          syncScreenTime(session.user.id);
        }
      });
    };
    resolveSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // [UIDIAG] auth event name only (TOKEN_REFRESHED after a long background
      // is part of the resume sequence the ui-health check reports on).
      addBreadcrumb('auth', 'auth state change', { event: String(_event) });
      // Same guard as resolveSession: while offline with a stored session, a
      // null-session event (INITIAL_SESSION fires before any refresh can run)
      // must not tear the hold down and route to /login. A real sign-out
      // clears storage, so hasStoredSession() is false and it passes through.
      if (!session && navigator.onLine === false && hasStoredSession()) return;
      // Session gone (sign-out, session expiry, account deletion): the whole
      // tree remounts to the landing page. Same clamp-strand risk as
      // handleSignOut — an unmounted focused field never fires focusout — so
      // always put the viewport back to normal scale range here. Harmless
      // no-op on the signed-out INITIAL_SESSION at cold start.
      if (!session) resetViewport();
      if (session) { offlineHold.current = false; setAuthOffline(false); }
      setUserStable(session?.user ?? null);
      setSentryUser(session?.user?.id ?? null);
      linkOneSignalUser(session?.user?.id ?? null);
      if (session?.user?.id) {
        ensurePushPermission();
        syncHealthWorkouts(session.user.id);
        syncDailyActivity(session.user.id);
        syncSleep(session.user.id);
        syncScreenTime(session.user.id);
      }
    });
    // The 'online' event is not delivered while iOS keeps the WebView
    // suspended in the background, so a reconnect that happens there would be
    // missed — the visibilitychange re-check on foreground covers it. (The
    // auth auto-refresh ticker heals the token independently either way.)
    const retry = () => { if (offlineHold.current && navigator.onLine !== false) resolveSession(); };
    const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ── LIVE THEME WATCHER (2026-09-28) ──────────────────────────────────────────
  // T/G/themeMode are resolved once at module load, so a live OS appearance
  // change — or one made while Scorify was backgrounded — does nothing until a
  // fresh JS load. When the EFFECTIVE theme diverges from what is rendered, we
  // reload (the sanctioned approach given the tokens are constants). Guards:
  //  • Only when it actually changed. effectiveTheme() returns the stored choice
  //    when set, so an OS flip is ignored unless the user's setting is System.
  //  • Never mid-input: defer while a [role=dialog] (modal, check-in, coach
  //    drawer) is on screen or a text field is focused — a reload would drop
  //    typed input. Re-checked on every trigger and by a short pending poll.
  //  • The App Lock is NOT bypassed: a reload re-mounts LockGate, which boots to
  //    the opaque crimson cover and re-locks from the Keychain (grace is
  //    in-memory, lost on reload) — the lock re-asserts, it is never skipped,
  //    and the boot cover hides the switch. So a reload is safe with the lock up.
  useEffect(() => {
    let pending = false;
    let pollTimer = null;
    const safeToReload = () => {
      try {
        // Never reload while the lock/boot cover is up: it would interrupt PIN
        // entry or flash past the lock. (The reload re-locks anyway, but we
        // wait for a clean moment.)
        if (window.__scorifyLockActive) return false;
        if (document.querySelector('[role="dialog"]')) return false;
        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return false;
      } catch {}
      return true;
    };
    const clearPoll = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const apply = () => {
      if (effectiveTheme() === themeMode) { pending = false; clearPoll(); return; }
      pending = true;
      if (document.visibilityState !== 'visible') return;   // wait for resume
      if (!safeToReload()) {
        // Defer until the modal/field closes; poll cheaply until it clears.
        if (!pollTimer) pollTimer = setInterval(() => { if (pending) apply(); }, 1000);
        return;
      }
      clearPoll();
      window.location.reload();
    };
    const onVis = () => { if (document.visibilityState === 'visible') apply(); };
    let mq = null;
    try { mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)'); } catch {}
    try { mq && mq.addEventListener('change', apply); } catch {}
    document.addEventListener('visibilitychange', onVis);
    // Native resume: iOS may not deliver matchMedia 'change' or visibilitychange
    // reliably while suspended, so re-check when the app becomes active again.
    let sub;
    (async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        sub = await CapacitorApp.addListener('appStateChange', ({ isActive }) => { if (isActive) apply(); });
      } catch {}
    })();
    return () => {
      try { mq && mq.removeEventListener('change', apply); } catch {}
      document.removeEventListener('visibilitychange', onVis);
      clearPoll();
      try { sub && sub.remove(); } catch {}
    };
  }, []);

  const handleSignOut = async () => {
    // Reset the viewport BEFORE teardown. Unmounting a focused input fires no
    // focusout, so the focus-zoom guard's maximum-scale=1 clamp would never
    // release — the landing page then renders zoomed with pinch locked until a
    // force-quit. resetViewport blurs while the field still exists, snaps
    // scale back to 1, and releases the clamp to the normal zoomable range.
    resetViewport();
    // The app-lock PIN SURVIVES sign-out: it is stored per user (keyed by uid),
    // so the same user signing back in on this phone reuses it with no setup, and
    // a different user transparently gets their own. The PIN is cleared only on
    // account deletion, the user disabling it, or the Forgot-PIN / 5-attempt
    // reset (which the lock screen handles). So sign-out no longer touches it.
    // Privacy: a signed-out phone must stop receiving this account's pushes.
    // The alias stays (see linkOneSignalUser); the device's push subscription
    // is opted OUT instead, and the next sign-in opts it back in.
    if (Capacitor.isNativePlatform()) {
      try {
        const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
        await applyPushIdentity(OneSignal, 'sign_out');
      } catch (e) { console.log('OneSignal opt-out error', e); }
    }
    try {
      // scope: 'local' — sign out ONLY this device. The default 'global'
      // revokes the refresh token on EVERY device (SCORIFY-APP-2: signing out
      // on a tester phone killed the everyday phone's session, degrading Money
      // to "not tracked" then bouncing it to sign-in). One device signing out
      // must never end another device's session.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.log("signOut error", e);
      reportError(e, { op: "auth.signOut" });
    }
    // RevenueCat follows the account off the device (never throws).
    await logOutPurchases();
    // Clear identity BEFORE state is torn down, so anything that errors after
    // this point is not attributed to the account that just signed out.
    setSentryUser(null);
    // Purge all user-scoped localStorage keys (coach msgs, caches, etc.)
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if ((k.startsWith('scorify_') || k.startsWith('ls_')) && k !== 'scorify_theme' && k !== OS_EXT_KEY) {
          toRemove.push(k);
        }
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch {}
    setUser(null);
    // Defer navigation to the next tick so the auth state settles and
    // the heavy landing page doesn't mount in the same synchronous frame
    // (prevents the WebView from hanging on the splash after sign out).
    setTimeout(() => {
      navigate("/");
    }, 50);
  };

  const handleAuthenticated = (authUser) => {
    setUser(authUser);
    navigate("/app");
  };

  if (user === undefined) return <Spinner offline={authOffline} />;

  return (
    <>
    {/* Mounted only for a signed-in user on the native app: web and demo
        mode never see the lock. Keyed by user id so an account switch gets a
        fresh boot pass (opaque cover until the Keychain answers). */}
    {user && Capacitor.isNativePlatform() && (
      <LockGate key={user.id} user={user} onSignOut={handleSignOut} />
    )}
    <Routes>
      <Route path="/" element={
        user
          ? <Navigate to="/app" />
          : <LandingPage
              onSignIn={() => navigate("/login")}
              onViewDemo={() => navigate("/demo")}
              onJoinWaitlist={async (email) => {
                const trimmed = (email || "").trim().toLowerCase();
                if (!trimmed) return;
                await fetch("/api/waitlist", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: trimmed }),
                }).catch((e) => { reportError(e, { op: "api.waitlist", endpoint: "/api/waitlist" }); });
              }}
            />
      } />
      <Route path="/login" element={
        user
          ? <Navigate to="/app" />
          : <Auth
              onAuthenticated={handleAuthenticated}
              onBack={() => navigate("/")}
              onDemo={() => navigate("/demo")}
            />
      } />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app" element={
        user
          ? <Scorify user={user} onSignOut={handleSignOut} />
          : <Navigate to="/login" />
      } />
      <Route path="/demo" element={<Scorify isDemo={true} />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}

// ── Dynamic Type bridge ─────────────────────────────────────────────────────
// The app's fixed-px typography ignores the iOS text-size setting entirely, and
// WKWebView never scales CSS px on its own. Bridge: measure `font:
// -apple-system-body` (WKWebView resolves it against the user's preferred
// content size; 17px at the default setting) and apply the ratio through
// `-webkit-text-size-adjust`, which scales TEXT ONLY — boxes with auto sizing
// reflow, fixed-height chrome stays put. Capped at 160%: beyond that the fixed
// layout genuinely breaks, and a capped-but-larger rendering beats a pretend
// uncapped one that clips. On non-iOS engines the probe resolves to a static
// size, the ratio is ~1, and this is a no-op.
const applyTextScale = () => {
  try {
    const probe = document.createElement('span');
    probe.style.cssText = 'font:-apple-system-body;position:absolute;visibility:hidden;pointer-events:none;';
    probe.textContent = 'M';
    document.body.appendChild(probe);
    const px = parseFloat(getComputedStyle(probe).fontSize) || 17;
    document.body.removeChild(probe);
    const scale = Math.min(Math.max(px / 17, 1), 1.6);
    document.documentElement.style.webkitTextSizeAdjust =
      scale > 1.02 ? `${Math.round(scale * 100)}%` : '100%';
  } catch { /* never let the bridge break rendering */ }
};

export default function App() {
  useEffect(() => {
    // index.html ships with a crimson <html> background so the pre-React
    // window of a cold launch matches the native launch screen. Once React
    // has painted (full-viewport components own every pixel now), hand the
    // document canvas back to the theme so overscroll inside #root shows
    // T.bg, not crimson.
    try { document.documentElement.style.background = T.bg; } catch {}
  }, []);

  useEffect(() => {
    // Dynamic Type: apply on launch and re-measure whenever the app comes back
    // to the foreground — the text-size setting changes in Settings or Control
    // Center, both of which background the webview, so visibilitychange is the
    // moment a new value can exist.
    applyTextScale();
    const onVisible = () => { if (document.visibilityState === 'visible') applyTextScale(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      try {
        // eslint-disable-next-line no-undef
        const OneSignal = window.plugins?.OneSignal || require('onesignal-cordova-plugin').default;
        OneSignal.initialize("40ed8e9c-136a-42b4-9da5-59abfa562336");
        // Open-tracking: tapping a push reports the notification's OneSignal id
        // back so notify-cron's rule (b) suppression (two consecutive unopened
        // sends of a type → 7-day mute) finally has real data — the
        // notification_log.opened column existed from day one with nothing
        // writing it. Fire-and-forget on purpose: a failed report must never
        // affect the tap's navigation, and the server scopes the write to the
        // authed user's own rows.
        // Foreground display: iOS suppresses banners while the app is open by
        // default, so a notification that arrived with Scorify foregrounded
        // vanished silently (device 2026-09-22). Registering this listener and
        // NOT calling preventDefault tells the plugin to proceed with the
        // system banner even in the foreground, so it always surfaces; tapping
        // it runs the click handler below (coach tap-through).
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', () => {
          // Intentionally no preventDefault — let iOS show the banner.
        });
        OneSignal.Notifications.addEventListener('click', async (event) => {
          // Tap-through: stash the notification's full text so it survives the
          // lock screen and a cold launch, then let Scorify reopen the coach
          // drawer with it once the app is unlocked and interactive. localStorage
          // (not React state) because this handler lives above the app tree and
          // the tap may cold-launch the process. Dispatched too, for the warm
          // case where Scorify is already listening.
          try {
            const extra = event?.notification?.additionalData || {};
            const osId = event?.notification?.notificationId || null;
            // Routing is pure (src/lib/pushActions.js routeNotificationClick):
            // an action button ("Mark done" / "Mark paid") ALWAYS wins and never
            // opens the coach chat, even when its data is malformed (Scorify then
            // says it could not find the habit). Body taps keep their routes:
            // briefing / recap open the pre-generated briefing (plan_week sets
            // plan), the evening mood prompt opens the mood sheet, and any other
            // Kai alert reopens its text in the coach drawer.
            const routed = routeNotificationClick({ actionId: event?.result?.actionId, data: extra, body: event?.notification?.body, notificationId: osId, now: Date.now() });
            // Shape only, never content: lets a mis-routed button tap be diagnosed from Sentry.
            addBreadcrumb('push', 'notification click', { route: routed.route, hasActionId: !!event?.result?.actionId, hasActionSelected: !!extra.actionSelected, stType: extra.st_type || null });
            if (routed.route === 'action') {
              writePendingAction(routed.pending);
            } else if (routed.route === 'briefing') {
              try { localStorage.setItem('scorify_pending_briefing', JSON.stringify(routed.pending)); } catch {}
              try { window.dispatchEvent(new CustomEvent('scorify-briefing-notification', { detail: routed.pending })); } catch {}
            } else if (routed.route === 'mood') {
              try { localStorage.setItem('scorify_pending_mood', JSON.stringify(routed.pending)); } catch {}
              try { window.dispatchEvent(new CustomEvent('scorify-mood-notification', { detail: routed.pending })); } catch {}
            } else if (routed.route === 'bank_reconnect') {
              try { localStorage.setItem(PENDING_BANK_RECONNECT_KEY, JSON.stringify(routed.pending)); } catch {}
              try { window.dispatchEvent(new CustomEvent(BANK_RECONNECT_EVENT, { detail: routed.pending })); } catch {}
            } else if (routed.route === 'coach') {
              try { localStorage.setItem('scorify_pending_coach', JSON.stringify(routed.pending)); } catch {}
              try { window.dispatchEvent(new CustomEvent('scorify-coach-notification', { detail: routed.pending })); } catch {}
            }
          } catch { /* never let tap-through break the click */ }
          // Open-tracking (unchanged): report the tap so rule (b) suppression has data.
          try {
            const osId = event?.notification?.notificationId;
            if (!osId) return;
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return; // signed out — nothing to attribute
            fetch('https://www.scorify.to/api/notification-opened', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ onesignal_id: String(osId) }),
            }).catch(() => {});
          } catch { /* never let open-reporting break the click */ }
        });
      } catch (e) {
        console.log('OneSignal init error', e);
      }
    }
  }, []);

  return (
    <BrowserRouter>
      {window.__DEMO_MODE__ ? <DemoApp /> : <AppRoutes />}
    </BrowserRouter>
  );
}
